// ── GeoNames TSV parser ───────────────────────────────────────────────────────
//
// Parses files downloaded from https://download.geonames.org/export/zip/
// One file per country, e.g. EE.txt, ES.txt, DE.txt
//
// Format (tab-separated, no header):
// country | postal_code | place_name | admin_name1 | admin_code1 |
// admin_name2 | admin_code2 | admin_name3 | admin_code3 |
// latitude | longitude | accuracy
import { withBase } from './MapSharedData';

export interface PostcodeEntry {
  country: string; // "EE"
  postcode: string; // "74305"
  place: string; // "Kehra"
  region: string; // admin_name1 → county/maakond (e.g. "Harju maakond")
  district: string; // admin_name2 → municipality/vald (e.g. "Anija vald")
  lat: number;
  lng: number;
}

export type PostcodeIndex = Map<string, PostcodeEntry>;

/**
 * Normalize a postcode for matching: uppercase, no whitespace, no dashes.
 *
 * Luxembourg special case: codes arrive both bare ("1626") and with the
 * country letter prefix "L-" ("L-1111", or "L1111" once the dash is stripped).
 * GeoNames LU.txt keys are the bare 4 digits, and prefix-shortening trims the
 * RIGHT side so it can't drop a leading letter. We strip a single leading "L"
 * when it's followed by exactly four digits — the Luxembourg code shape — so
 * "L1111" → "1111" without touching letter-based codes elsewhere (e.g. Malta's
 * "VLT", UK's "EC1").
 *
 * Argentina special case: the modern CPA format is a letter (province) + 4
 * digits + 3 letters, e.g. "C1425BGX". GeoNames AR.txt keys are the legacy
 * 4-digit CPC ("1425"), so we reduce a full CPA to its 4 digits. The pattern
 * is strict (^[A-Z]\d{4}[A-Z]{3}$) so it only fires on a complete CPA and
 * leaves everything else — including bare 4-digit CPCs ("1877") and other
 * countries' alphanumeric codes — untouched.
 *
 * Armenia special case: until April 2006 Armenia used 6-digit Soviet-era codes
 * in the range 375000–378999, linearly offset from the modern 4-digit codes by
 * 375000 (the official example: Yerevan 375010 → 0010). Some backend data still
 * carries the old form (e.g. "375002"). We convert by subtracting 375000 and
 * zero-padding to 4 digits (375002 → 0002, 377000 → 2000). The pattern
 * ^37[5-8]\d{3}$ is narrow; and since lookups are scoped per-country (these are
 * matched against AM.txt only), there's no collision with other countries'
 * 6-digit codes.
 */
function normalize(pc: string): string {
  let base = pc.toUpperCase().replace(/[\s-]/g, '');

  // Each rule is gated on a narrow shape that is unique to one country's
  // postcode format, so it can only ever fire on that country's codes — no
  // need to thread countryCode through the call sites. Crucially, lookups run
  // against a single country's index at a time, so even if a shape coincided,
  // a foreign code would still only be searched in its own country's file.

  // Luxembourg: bare ("1626") or with country letter "L-" ("L-1111" → "L1111").
  // Strip a leading "L" before exactly 4 digits.
  base = base.replace(/^L(?=\d{4}$)/, '');

  // Azerbaijan: "AZ0300" / "AZ 0300" / bare "0300". Strip a leading "AZ"
  // before exactly 4 digits (two-letter guard leaves "LV", "VLT" untouched).
  base = base.replace(/^AZ(?=\d{4}$)/, '');

  // Argentina CPA → 4-digit CPC (C1425BGX → 1425).
  const cpa = base.match(/^[A-Z](\d{4})[A-Z]{3}$/);
  if (cpa) return cpa[1];

  // Armenia: pre-2006 6-digit Soviet codes (375000–378999) offset by 375000
  // from the modern 4-digit codes (Yerevan 375010 → 0010).
  if (/^37[5-8]\d{3}$/.test(base)) {
    const n = parseInt(base, 10) - 375000;
    if (n >= 0 && n <= 9999) return String(n).padStart(4, '0');
  }

  return base;
}

/**
 * Look up a postcode in the index with progressive fallback:
 *
 *  1. Exact match (original casing/whitespace preserved)
 *  2. Normalized match (uppercase, no whitespace/dashes)
 *  3. Prefix match (trim normalized key character by character until found)
 *
 * Step 3 handles countries where GeoNames data is aggregated to a shorter
 * prefix than the real postcode — e.g. Malta has only "VLT" in GeoNames
 * but real postcodes are "VLT 1010". With prefix matching, "VLT 1010" → "VLT".
 *
 * Returns the matched entry or null.
 */
export function lookupPostcode(index: PostcodeIndex, postcode: string): PostcodeEntry | null {
  // 1. Exact
  let entry = index.get(postcode);
  if (entry) return entry;

  // 2. Normalized
  const norm = normalize(postcode);
  entry = index.get(norm);
  if (entry) return entry;

  // 3. Progressive prefix shortening — try ever-shorter prefixes of the
  //    normalized key, but stop at 2 chars to avoid false positives.
  for (let len = norm.length - 1; len >= 2; len--) {
    const prefix = norm.slice(0, len);
    entry = index.get(prefix);
    if (entry) return entry;
  }

  return null;
}

/**
 * Parse raw GeoNames TSV text into a Map keyed by postcode.
 * Each entry is indexed both under its raw postcode AND its normalized form
 * so that lookups work with messy input (extra whitespace, different casing).
 */
export function parseGeonames(tsvText: string): PostcodeIndex {
  const index: PostcodeIndex = new Map();
  const lines = tsvText.split('\n');

  for (const line of lines) {
    if (!line.trim()) continue;
    const cols = line.split('\t');
    if (cols.length < 11) continue;

    const lat = parseFloat(cols[9]);
    const lng = parseFloat(cols[10]);
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;

    const entry: PostcodeEntry = {
      country: cols[0],
      postcode: cols[1],
      place: cols[2],
      region: cols[3] || 'Unknown region',
      district: cols[5] || cols[3] || 'Unknown',
      lat,
      lng,
    };
    // Index under both raw and normalized keys so direct .get() still works
    // (used by some call sites) AND lookupPostcode() can short-circuit on the
    // normalized path.
    index.set(entry.postcode, entry);
    const norm = normalize(entry.postcode);
    if (norm && norm !== entry.postcode && !index.has(norm)) {
      index.set(norm, entry);
    }
  }
  return index;
}

/**
 * Lazily fetch and parse GeoNames CSV for a country.
 * Results are cached in module scope so the same country isn't refetched.
 */
const cache = new Map<string, Promise<PostcodeIndex>>();

export function loadCountryPostcodes(
  countryCode: string,
  baseUrl = '/geonames',
): Promise<PostcodeIndex> {
  const key = countryCode.toUpperCase();
  let p = cache.get(key);
  if (p) return p;

  p = (async () => {
    const res = await fetch(withBase(`${baseUrl}/${key}.txt`));
    if (!res.ok) throw new Error(`Failed to load ${key}.txt: HTTP ${res.status}`);
    const text = await res.text();
    const index = parseGeonames(text);

    // China: real shipping codes are full 6-digit (e.g. 516211 Huizhou) and
    // aren't present in CN.txt as exact keys. lookupPostcode shortens the
    // queried code right-to-left and needs an exact key match, so we load a
    // separate CN-prefixes.txt holding 3-digit city-level prefix keys (516 ->
    // Huizhou) and merge them in. CN.txt itself is left untouched. Real 6-digit
    // codes still resolve via the prefix fallback: 516211 -> 516.
    if (key === 'CN') {
      try {
        const pref = await fetch(withBase(`${baseUrl}/CN-prefixes.txt`));
        if (pref.ok) {
          const prefIndex = parseGeonames(await pref.text());
          for (const [k, v] of prefIndex) {
            // Don't overwrite any real CN.txt keys — prefixes only fill gaps.
            if (!index.has(k)) index.set(k, v);
          }
        }
      } catch {
        // Prefix file missing is non-fatal — CN just falls back to exact codes.
      }
    }

    return index;
  })();

  cache.set(key, p);
  return p;
}

/** Clear cache (useful for tests or manual refresh). */
export function clearPostcodeCache() {
  cache.clear();
}
